import { useState } from 'react';
import { HelpCircle, Loader2, Sparkles } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface MetricExplanationButtonProps {
  metricName: string;
  value?: number | string;
  className?: string;
}

export function MetricExplanationButton({ metricName, value, className }: MetricExplanationButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [conceptName, setConceptName] = useState<string | null>(null);

  const fetchExplanation = async () => {
    if (explanation) return; // Already fetched

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('explain-metric', {
        body: { metricName, userValue: value },
      });

      if (error) throw error;

      setExplanation(data.explanation);
      setConceptName(data.concept);
    } catch (error) {
      console.error('Failed to fetch explanation:', error);
      setExplanation('Unable to load explanation at this time.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      fetchExplanation();
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`h-5 w-5 text-muted-foreground hover:text-primary ${className}`}
          aria-label={`Explain ${metricName}`}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 overflow-hidden" align="start">
        <div className="bg-primary/5 p-3 border-b border-primary/10 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm text-primary">
            {conceptName || metricName}
          </span>
        </div>
        <div className="p-4 text-sm leading-relaxed">
          {loading ? (
            <div className="flex items-center justify-center py-4 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Generating explanation...
            </div>
          ) : (
            <p>{explanation}</p>
          )}
        </div>
        <div className="bg-muted/30 p-2 text-[10px] text-muted-foreground text-center border-t">
          Educational content only. Not investment advice.
        </div>
      </PopoverContent>
    </Popover>
  );
}
